import React, { useCallback, useEffect, useState } from "react";
import Header from "../../header/header";
import Footer from "../../footer/footer";
import {
  useNavigate,
  useParams,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import makeRequest from "../../utils/fetch-request";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { getFromLocalStorage } from "../../utils/local-storage";
import useAnalyticsEventTracker from "../../analytics/useAnalyticsEventTracker";
import FullscreenButton from "../../shared/FullScreenButton";
import useWindowDimensions from "../../header/Dimensions";
import { useDispatch, useSelector } from "react-redux";
import {
  casinoCreatePlayer,
  casinoGamePlay,
  casinoGameProviderPlay,
} from "../../../redux/virtualsSlice";
import { Switch } from "@mui/material"; // Import the MUI Switch component

const GamePlay = React.memo(() => {
  // const gaEventTracker = useAnalyticsEventTracker("Casino Game");
  const location = useLocation();
  const dispatchRedux = useDispatch();

  const [user] = useState(getFromLocalStorage("user"));
  const [isLoggedIn] = useState(getFromLocalStorage("user"));

  const gameUrlLoaded = useSelector((state) => state.virtuals.fetching) ?? true;
  const gameUrl = useSelector((state) => state.virtuals.casino_game_url);

  const [demoUrlLoaded, setDemoUrlLoaded] = useState(false); // For demo

  // Parse the query paramsf
  const queryParams = new URLSearchParams(location.search);
  const status = queryParams.get("status"); // 1 for real, 0 for demo
  const category = queryParams.get("category");
  const game_url = atob(queryParams.get("game_url")); // Real game URL
  let demo_url = atob(queryParams.get("demo_url")); // Demo game URL
  const game_id = queryParams.get("game_id"); //  game id
  const provider = queryParams.get("provider");
  const [searchParams, setSearchParams] = useSearchParams();

  // Define the CSS style for the iframe
  const { width } = useWindowDimensions();
  const [isCustomFullscreen, setCustomFullscreen] = useState(false);
  const [useDemo, setUseDemo] = useState(status === "1"); // Track switch state: true for real, false for demo game
  const [iframeHeight, setIframeHeight] = useState(width < 991 ? 90 : 78); // Initial height

  // Define the CSS style for the iframe
  const iframeStyle = {
    maxWidth: "100%",
    width: width < 991 ? "100lvw" : "100%", // Set width to 100% for mobile, undefined for desktop
    height: `${iframeHeight}svh`, // Set the height dynamically
  };
  const maxIframeHeight =
    width > 991
      ? isCustomFullscreen
        ? window.innerHeight * 2.5
        : window.innerHeight * 0.82
      : window.innerHeight * 2.5; // Maximum height is 77% desktop  and 92% mobile of the screen height

  // // Function to update the iframe height
  const updateIframeHeight = useCallback(() => {
    console.log("customFullscrren", isCustomFullscreen);
    if (isCustomFullscreen) {
      console.log("customFullscrren now", maxIframeHeight);

      setIframeHeight(width < 991 ? 95 : 90); // Set the fixed height here
    } else {
      setIframeHeight(width < 991 ? 90 : 78);
    }
  }, [isCustomFullscreen]);

  // Handle Switch state change
  const handleSwitchChange = (event) => {
    const isChecked = event.target.checked;
    setUseDemo(isChecked);

    // Update the query param
    const newParams = new URLSearchParams(searchParams);
    newParams.set("status", isChecked ? "1" : "0");
    setSearchParams(newParams);
  };

  useEffect(() => {
    // Initial iframe height calculation
    updateIframeHeight();

    // Update iframe height when the window is resized
    window.addEventListener("resize", updateIframeHeight);

    // Clean up the event listener when the component is unmounted
    return () => {
      window.removeEventListener("resize", updateIframeHeight);
    };
  }, [updateIframeHeight, isCustomFullscreen]);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") {
        setCustomFullscreen(false);
      }
    };

    window.addEventListener("keydown", handleEsc);

    return () => {
      window.removeEventListener("keydown", handleEsc);
    };
  }, []); // Empty dependency array to run the effect only on mount and unmount

  const toggleFullscreen = () => {
    const element = document.documentElement; // Fullscreen the whole document
    // console.log("Element fullscreen is now ... ",element)

    if (!isCustomFullscreen) {
      try {
        if (element?.requestFullscreen) {
          element?.requestFullscreen();
        } else if (element?.mozRequestFullScreen) {
          element?.mozRequestFullScreen();
        } else if (element?.webkitRequestFullscreen) {
          element?.webkitRequestFullscreen();
        } else if (element?.msRequestFullscreen) {
          element?.msRequestFullscreen();
        }
      } catch (err) {
        //there was an error encountered
        console.error("error_message", err);
      }

      setCustomFullscreen(true);
    } else {
      try {
        if (!document.fullscreenEnabled) {
          setCustomFullscreen(false);
        }
        if (document.fullscreenElement) {
          if (document.exitFullscreen) {
            document.exitFullscreen();
          } else if (document.mozCancelFullScreen) {
            document.mozCancelFullScreen();
          } else if (document.webkitExitFullscreen) {
            document.webkitExitFullscreen();
          } else if (document.msExitFullscreen) {
            document.msExitFullscreen();
          }
        }
      } catch (err) {
        console.error("error_encountered", err);
      }

      setCustomFullscreen(false);
    }
  };

  const createPlayer = async () => {
    if (provider === "pragmatic") {
      // Return a promise so that `startGame` can be called afterward
      return dispatchRedux(casinoCreatePlayer());
    } else {
      // Immediately resolve for other providers
      return Promise.resolve();
    }
  };

  const startGame = async () => {
    const isPragmatic = provider?.toLowerCase() === "pragmatic";
    const isSAGaming = provider?.toLowerCase() === "sagaming";

    if (status === "1" || useDemo || isSAGaming) {
      // For real play or sagaming (same flow for demo/real), or demo

      if (isPragmatic) {
        const gameUrlWithId = `${game_url}?game_id=${game_id}`;
        const data = {
          endpoint: gameUrlWithId,
          method: "GET",
        };
        dispatchRedux(casinoGamePlay(data));
      } else {
        const gameUrlWithId = `${game_url}`;
        const payload = {
          token: user?.token,
          profile_id: user?.profile_id,
          game: game_id,
          type: status === "1" ? 1 : 0,
          gameCategory: category,
        };
        const data = {
          endpoint: gameUrlWithId,
          method: "POST",
          data: payload,
        };
        dispatchRedux(casinoGameProviderPlay(data));
      }
    } else if (status === "0" && !useDemo) {
      // Possibly unreachable now due to above logic but kept for fallback
      setDemoUrlLoaded(true);
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      createPlayer().then(() => {
        startGame(); // Only call startGame if it's a real game
      });
    } else {
      window.location.href = "/login";
    }
  }, [isLoggedIn, useDemo]);

  return (
    <>
      <Header />
      <div className={user ? "user_logged casino" : "amt-casino"}>
        <FullscreenButton
          onClick={toggleFullscreen}
          navigation={"/casino"}
          isCustomFullScreen={isCustomFullscreen}
        />
        <div className="d-flex flex-row justify-content-between">
          <div className="col-md-12 virtual-width-mobile">
            <div className="homepage mt-2">
              <div
                className={`col-md-12 ${gameUrlLoaded ? "d-none" : "d-block"}`}
              >
                <SkeletonTheme baseColor="#0e131b" highlightColor="#3f6878">
                  <Skeleton height={"100px"} />
                </SkeletonTheme>
              </div>
              {(gameUrlLoaded || demoUrlLoaded) && (
                <div
                  className={` ${
                    isCustomFullscreen ? "active custom-fullscreen-wrapper" : ""
                  }`}
                >
                  <iframe
                    className={"mt-3 shadow-lg"}
                    id={"casinoGamePlay"}
                    src={
                      useDemo
                        ? gameUrl
                        : provider === "sagaming"
                        ? gameUrl
                        : demo_url
                    } // Directly use demo_url for demo games
                    title="Game"
                    style={{
                      ...iframeStyle,
                      height: `${Math.min(iframeHeight, maxIframeHeight)}svh`,
                    }}
                  ></iframe>
                </div>
              )}
            </div>
          </div>
        </div>
        <div
          className={`d-flex align-items-center justify-content-center w-100 switch-games-casino ${
            isCustomFullscreen ? "" : "inactive"
          }`}
        >
          <span className={`ml-2 ${!useDemo ? "text-light" : "text-grey"}`}>
            Demo Play{" "}
          </span>
          <Switch
            checked={useDemo}
            onChange={handleSwitchChange}
            inputProps={{ "aria-label": "controlled" }}
          />
          <span className={`ml-2 ${useDemo ? "text-light" : "text-grey"}`}>
            Real Play
          </span>
        </div>
        {game_id.includes("aviator") && (
          <div
            className={"card rounded-3 e "}
            style={{
              color: "#999",
              background: "transparent",
              textDecoration: "none",
              listStyle: "none",
              fontSize: "14px",
            }}
          >
            <div className={"card-body p-3"}>
              <h3 className={"text-center text-warning flashy"}>
                WIN Upto 6,000,000/= na AVIATOR daily.
              </h3>
              <br />
              <h3 className={"text-center"}>How to Play Aviator</h3>
              <ul>
                <li>
                  Fly Aviator to WIN Upto 6,000,000/= per Round. Choose your bet
                  amount and press confirm. Use AUTO-BET function for a
                  guaranteed WIN. Minimum Bet – KSH 10. Maximum Bet – KSH
                  30,000. Maximum Win per Round is KSH 6,000,000. Maximize your
                  Winnings with the Double Bet feature by adding another bet
                  anytime and set different values.
                </li>
                <li>
                  Once the countdown expires, the Aviator will take flight and
                  start collecting a growing WIN multiplier. Press the “Cashout”
                  button at any point to claim your winnings.
                </li>
                <li>
                  When using the “AUTO CASHOUT”, money will be withdrawn when
                  the MULTIPLIER exceeds the chosen AUTO-CASHOUT index. Example;
                  If you had chosen 1.5X Index, it will cashout automatically at
                  1.5X and WINNINGS credited in your account.
                </li>
              </ul>
              <br />
              <h3 className={"text-center"}>How to Win Aviator</h3>
              <ul>
                <li>
                  Press the Cashout button before the Aviator Plane flies away.
                </li>
                <li>
                  Aviator features a Double Bet Feature that allows a second bet
                  during play. The second bet is set and activated independently
                  of the first one and can be cashed out without cashing the
                  initial set bet.
                </li>
              </ul>
              <br />
              <h3 className={"text-center"}>Aviator Freebets </h3>
              <ul>
                <li>Play Aviator on BetNare & Get Freebets every time.</li>
                <li>
                  Register now on https://betnare.com/nare-games/aviator and
                  become a potential winner.
                </li>
                <li>
                  Sign Up to play Aviator here
                  https://betnare.com/nare-games/aviator .
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>
      <div className={"footer-mobile-none"}>
        <Footer />
      </div>
    </>
  );
});

export default React.memo(GamePlay);
